import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { aceApi } from 'api/ace';
import { addYears, parse } from 'date-fns';
import { toPairs, forEach } from 'ramda';
import { BookAppointmentResponse, Booking } from 'types';
import { toggleFrozenAppointmentInStorage, saveBookedAppointmentDetailsInStorage } from 'utils/appointment';
import { dateFormats, formatDate } from 'utils/date';
import { getBookAppointmentTypeForReason } from 'utils/reason';

import { useAppOptions } from './use-app-options';
import { useProviders } from './use-providers';

const useBookAppointment = (booking: Booking): UseMutationResult<BookAppointmentResponse> => {
    const { practice, type } = useAppOptions();
    const { data: providers } = useProviders(booking);
    const {
        patientInformation,
        reasonDetails,
        appointment,
        departmentAndRelatedDepartments,
        reason,
        firstTimeWorkersCompInjuryCheck,
        reasonForVisit,
    } = booking;

    const provider = providers.find(({ providerid }) => providerid === appointment?.providerid);
    const department = departmentAndRelatedDepartments.find(({ departmentid }) => departmentid === appointment?.departmentid);

    const bookingAppointmentTypeId =
        appointment && reason ? getBookAppointmentTypeForReason(reason, appointment.departmentid, appointment.providerid) : null;

    const bodyFormData = new FormData();

    const params: Record<string, string | undefined | boolean | number> = {
        address1: patientInformation?.address,
        address2: patientInformation?.addressCount,
        appointmentid: appointment?.appointmentid,
        bookingnote: reasonDetails,
        city: patientInformation?.city,
        departmentid: department?.departmentid ?? appointment?.departmentid,
        dob: patientInformation?.dateOfBirth
            ? formatDate(parse(patientInformation?.dateOfBirth, dateFormats.monthDayYear, new Date()), 'monthDayYear')
            : undefined,
        email: patientInformation?.email,
        expirationdate: formatDate(addYears(new Date(), 1), 'monthDayYear'),
        firstname: patientInformation?.firstName,
        ignoreschedulablepermission: false,
        insuredsignature: false,
        lastname: patientInformation?.lastName,
        primaryproviderid: provider?.providerid,
        schedulingnote: `${reason?.name}${reason?.is_in_person ? '' : ' - Telemedicine'}`,
        sex: patientInformation?.sex,
        signaturedatetime: formatDate(new Date(), 'monthDayYearWithTime'),
        state: patientInformation?.state,
        zip: patientInformation?.zip,
        ...(reason?.emr_reason_id && { reasonid: reason?.emr_reason_id }),
        ...(!reason?.emr_reason_id &&
            appointment?.appointmenttypeid && {
                appointmenttypeid: bookingAppointmentTypeId ?? appointment?.appointmenttypeid,
                ignoreschedulablepermission: true,
            }),
    };

    if (type === 'citymd') {
        params.nopatientcase = true;
        params.schedulingnote = patientInformation?.schedulingnote ?? '';
        params.reasonforvisit = reasonForVisit;
        params.firsttimeworkerscompinjurycheck = firstTimeWorkersCompInjuryCheck;
    }

    forEach<[keyof typeof params, (typeof params)[keyof typeof params]]>(([key, value]) => {
        if (value !== undefined) {
            bodyFormData.append(key, value.toString());
        }
    }, toPairs(params));

    if (patientInformation?.phoneType === 'Home') {
        bodyFormData.append('homephone', patientInformation.phoneNumber.toString());
    }

    if (patientInformation?.phoneType === 'Mobile') {
        bodyFormData.append('mobilephone', patientInformation.phoneNumber.toString());
    }

    return useMutation({
        mutationKey: ['book-appointment'],
        mutationFn: async () => {
            const response = await aceApi.post<BookAppointmentResponse['data']>(
                `/${booking.market?.market_key}/${practice}/athena/patient-appointment`,
                bodyFormData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            saveBookedAppointmentDetailsInStorage(booking);

            // Remove the booked appointment from "FreezeAppointment" storage
            if (appointment?.appointmentid) {
                toggleFrozenAppointmentInStorage({ appointmentid: appointment.appointmentid, freeze: false });
            }

            return response;
        },
    });
};

export { useBookAppointment };
